export interface ServiceProvider {
  id: string;
  name: string;
  type: "företag" | "hobby";
  rating: number;
  reviewCount: number;
  location: string;
  image: string;
  verified: boolean;
}

export interface Service {
  id: string;
  provider: ServiceProvider;
  serviceType: string;
  title: string;
  description: string;
  priceModel: "fast" | "timme" | "från";
  basePrice: number;
  travelFee?: string;
  availability: string;
  images: string[];
  certifications: string[];
}

export interface Listing {
  id: string;
  sellerName: string;
  category: "ved" | "julgran" | "plank" | "stockar" | "enskilda träd";
  species: "gran" | "tall" | "björk" | "ek" | "blandad";
  title: string;
  description: string;
  price: number;
  priceUnit: string;
  location: string;
  delivery: "hämta" | "leverans" | "båda";
  images: string[];
  status: "aktiv" | "såld" | "reserverad";
}

export interface Review {
  id: string;
  author: string;
  rating: number;
  text: string;
  date: string;
}

export const serviceTypes = [
  { value: "tradfallning", label: "Trädfällning" },
  { value: "gallring", label: "Gallring" },
  { value: "bortforsling", label: "Bortforsling" },
  { value: "kapning-ved", label: "Kapning/Ved" },
  { value: "transport", label: "Transport" },
  { value: "sagning", label: "Sågning" },
];

export const treeSpecies = [
  { value: "gran", label: "Gran" },
  { value: "tall", label: "Tall" },
  { value: "bjork", label: "Björk" },
  { value: "ek", label: "Ek" },
  { value: "blandad", label: "Blandad" },
];

export const listingCategories = [
  { value: "ved", label: "Ved" },
  { value: "julgran", label: "Julgran" },
  { value: "plank", label: "Plank" },
  { value: "stockar", label: "Stockar" },
  { value: "enskilda träd", label: "Enskilda träd" },
];

const providers: ServiceProvider[] = [
  { id: "p1", name: "Skogsexperten AB", type: "företag", rating: 4.8, reviewCount: 124, location: "Stockholm", image: "", verified: true },
  { id: "p2", name: "Norrlands Trädservice", type: "företag", rating: 4.6, reviewCount: 87, location: "Umeå", image: "", verified: true },
  { id: "p3", name: "Erik Johansson", type: "hobby", rating: 4.9, reviewCount: 32, location: "Göteborg", image: "", verified: false },
  { id: "p4", name: "Grönmark Skog", type: "företag", rating: 4.5, reviewCount: 56, location: "Malmö", image: "", verified: true },
  { id: "p5", name: "Anna Lindgren", type: "hobby", rating: 5.0, reviewCount: 15, location: "Uppsala", image: "", verified: false },
  { id: "p6", name: "Skogsvård Syd", type: "företag", rating: 4.7, reviewCount: 203, location: "Lund", image: "", verified: true },
];

export const services: Service[] = [
  { id: "s1", provider: providers[0], serviceType: "tradfallning", title: "Professionell trädfällning", description: "Vi fäller träd säkert i alla storlekar. Erfarna arborister med rätt utrustning. Vi hanterar allt från enstaka träd till större avverkningar.", priceModel: "från", basePrice: 2500, travelFee: "Ingår inom 30 km", availability: "Nästa vecka", images: [], certifications: ["Certifierad arborist", "Försäkrad"] },
  { id: "s2", provider: providers[1], serviceType: "gallring", title: "Skogsgallring & röjning", description: "Professionell gallring för bättre skogstillväxt. Vi hjälper dig att optimera din skog.", priceModel: "timme", basePrice: 850, travelFee: "250 kr/tillfälle", availability: "Inom 2 veckor", images: [], certifications: ["Skogsstyrelsen-godkänd"] },
  { id: "s3", provider: providers[2], serviceType: "kapning-ved", title: "Vedkapning & klyvning", description: "Kapa och klyv ditt virke till perfekt ved. Jag kommer hem till dig med mobil vedmaskin.", priceModel: "timme", basePrice: 500, availability: "Flexibel", images: [], certifications: [] },
  { id: "s4", provider: providers[3], serviceType: "transport", title: "Virkestransport", description: "Transport av virke, ved och stockar med flak. Hela Skåne och södra Sverige.", priceModel: "från", basePrice: 1500, travelFee: "12 kr/km", availability: "Samma vecka", images: [], certifications: ["Transporttillstånd"] },
  { id: "s5", provider: providers[4], serviceType: "bortforsling", title: "Bortforsling av ris & grenar", description: "Jag tar hand om allt ris och grenar efter avverkning. Miljövänlig hantering.", priceModel: "fast", basePrice: 3000, availability: "Inom 3 dagar", images: [], certifications: [] },
  { id: "s6", provider: providers[5], serviceType: "sagning", title: "Mobil sågverkstjänst", description: "Såga ditt eget virke till plank och brädor. Vi kommer till dig med mobilt sågverk.", priceModel: "timme", basePrice: 900, travelFee: "Ingår inom 50 km", availability: "Boka 1 vecka i förväg", images: [], certifications: ["Certifierad sågare"] },
];

export const listings: Listing[] = [
  { id: "l1", sellerName: "Bengt S.", category: "ved", species: "björk", title: "Björkved, torkad 1 år", description: "Fint kapad och kluven björkved. Torkad under tak i ett år. Perfekt för kaminen. Ca 2 kubikmeter.", price: 1800, priceUnit: "per kubik", location: "Norrtälje", delivery: "hämta", images: [], status: "aktiv" },
  { id: "l2", sellerName: "Maria K.", category: "stockar", species: "tall", title: "Tallstockar, 4 meter", description: "10 st tallstockar, nyavverkade. Diameter 20–30 cm. Bra kvalitet för sågning.", price: 500, priceUnit: "per stock", location: "Gävle", delivery: "båda", images: [], status: "aktiv" },
  { id: "l3", sellerName: "Anders P.", category: "plank", species: "ek", title: "Ekplank, lufttorkade", description: "Ekplank i olika dimensioner, lufttorkade i 2 år. Perfekt för möbelsnickeri.", price: 350, priceUnit: "per löpmeter", location: "Växjö", delivery: "båda", images: [], status: "aktiv" },
  { id: "l4", sellerName: "Lars O.", category: "enskilda träd", species: "ek", title: "Stor ek, ca 80 år", description: "Stor ek som behöver fällas. Fin stam, ca 60 cm diameter. Står lättillgängligt.", price: 0, priceUnit: "prisförslag", location: "Linköping", delivery: "hämta", images: [], status: "aktiv" },
  { id: "l5", sellerName: "Karin J.", category: "ved", species: "gran", title: "Granved, blandad storlek", description: "Granved i blandade längder. Passar bra för eldning i öppen spis eller vedpanna.", price: 1200, priceUnit: "per kubik", location: "Sundsvall", delivery: "hämta", images: [], status: "aktiv" },
  { id: "l6", sellerName: "Sven A.", category: "julgran", species: "gran", title: "Julgranar – välj och fäll själv!", description: "Kom och välj din egen julgran i vår plantering. Nordmansgran och rödgran, 1,5–3 meter.", price: 350, priceUnit: "per träd", location: "Sigtuna", delivery: "hämta", images: [], status: "aktiv" },
];

export const reviews: Review[] = [
  { id: "r1", author: "Johan M.", rating: 5, text: "Fantastiskt jobb! Snabbt och professionellt. Trädet var nere på en timme.", date: "2026-01-15" },
  { id: "r2", author: "Lisa S.", rating: 4, text: "Bra service och trevligt bemötande. Lite sen ankomst men resultatet var toppen.", date: "2026-01-10" },
  { id: "r3", author: "Peter A.", rating: 5, text: "Rekommenderas starkt! Mycket kunnig och noggrann. Städade efter sig.", date: "2025-12-20" },
  { id: "r4", author: "Eva L.", rating: 5, text: "Professionell från start till slut. Gav bra råd om vilka träd som borde sparas.", date: "2025-12-05" },
];
